import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./styles.module.scss";
import { ExpandableTable } from "@components/ExpandebleTable";
import { getData, getTableColumns } from "./utils";
import { TableTab } from "./Tabs/models";
import { Tabs } from "./Tabs";
import { useAdsAnaliticsStore } from "src/store/ads-analitics.store";
import { useDashboardStore } from "@pages/Dashboard/store/dashboard.state";
import { KeywordApi } from "@services/keywords/keywords.api";
import { formatDateRange } from "@components/SaturationCurve/utils";
import { IPerformanceTableDataCampaign } from "src/types/perfomanceTable";
import { SortToggle } from "./sortDirection";
import { useFeatureToggle } from "src/hooks/useFeatureToggle";

type RowData = IPerformanceTableDataCampaign & { level: number };

interface OrganicTableProps {
  isWhatIf?: boolean;
  autoHeight?: boolean;
}

export const OrganicTable: React.FC<OrganicTableProps> = ({
  isWhatIf = false,
  autoHeight = false,
}) => {
  const [data, setData] = useState<RowData[]>([]);
  const [totalRow, setTotalRow] = useState<RowData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selections, setSelections] = useState<React.Key[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [activeTab, setActiveTab] = useState<TableTab>(
    "Organic Keyword Performance",
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 8;

  const subscriptionPerformanceEnabled = useFeatureToggle(
    "SUBSCRIPTION_PERFORMANCE",
  );
  const allTabs: TableTab[] = ["Organic Keyword Performance"];
  const availableTabs = subscriptionPerformanceEnabled
    ? allTabs
    : allTabs.filter((t) => t !== "Current/Missing Target");

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [subscriptionPerformanceEnabled, activeTab, availableTabs]);

  const { isAsinSelect, dateRange } = useDashboardStore();
  const { adSpend: saturation } = useAdsAnaliticsStore().adSlides;
  const { date_start, date_end } = formatDateRange(
    dateRange.startDate,
    dateRange.endDate,
  );

  const perfParams = useMemo(
    () =>
      [
        date_start,
        date_end,
        saturation,
        "search_query_volume",
        sortDir,
        "",
        [],
      ] as const,
    [date_start, date_end, saturation, sortDir],
  );
  const keywordsApi = useMemo(() => new KeywordApi(), []);

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState(0);

  const recalc = useCallback(() => {
    const cont = containerRef.current,
      head = headerRef.current,
      tabs = tabsRef.current;
    if (!cont || !head || !tabs) return;
    setBodyHeight(
      autoHeight
        ? cont.clientHeight
        : cont.clientHeight - head.clientHeight - tabs.clientHeight,
    );
  }, [autoHeight]);

  useLayoutEffect(recalc, [recalc]);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(recalc);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [recalc]);

  const checkNeedMore = useCallback(() => {
    if (isLoading || !hasMore) return;
    const body =
      wrapperRef.current?.querySelector<HTMLDivElement>(".ant-table-body");
    if (!body) return;
    if (body.scrollHeight <= body.clientHeight + 1) {
      setOffset((p) => p + limit);
    }
  }, [isLoading, hasMore]);

  const handleScroll = useCallback(
    (e: Event) => {
      const target = e.currentTarget as HTMLElement;
      if (
        target.scrollHeight - target.scrollTop <= target.clientHeight + 20 &&
        !isLoading &&
        hasMore
      ) {
        setOffset((p) => p + limit);
      }
    },
    [isLoading, hasMore],
  );

  useEffect(() => {
    const body =
      wrapperRef.current?.querySelector<HTMLDivElement>(".ant-table-body");
    if (!body) return;
    body.addEventListener("scroll", handleScroll);
    return () => body.removeEventListener("scroll", handleScroll);
  }, [handleScroll, data, totalRow]);

  useEffect(() => {
    setData([]);
    setTotalRow(null);
    setSelections([]);
    setExpandedRowKeys([]);
    setOffset(0);
    setHasMore(true);
  }, [activeTab, isAsinSelect, date_start, date_end, saturation]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        if (activeTab === "Organic Keyword Performance") {
          const rows = isAsinSelect
            ? await keywordsApi.getKeywordPerfomanceAsin(
                ...perfParams,
                offset,
                limit,
              )
            : await keywordsApi.getKeywordPerfomanceBrand(
                ...perfParams,
                offset,
                limit,
              );
          if (cancelled) return;

          const totals = rows.filter((r) => r.search_query === "Total");
          if (!totalRow && totals[0]) {
            const t = totals[0];
            setTotalRow({ ...t, key: t.key ?? t.search_query, children: [] });
          }
          const pageRows = rows.filter((r) => r.search_query !== "Total");
          const mapped: RowData[] = pageRows.map((r) => ({
            ...r,
            key: r.key ?? r.search_query,
            children: [],
            level: 0,
          }));

          setData((prev) => (offset === 0 ? mapped : [...prev, ...mapped]));

          if (pageRows.length < limit) {
            setHasMore(false);
          }
        } else {
          const staticRows = getData(activeTab) as any[];
          setData(
            staticRows.map((r, idx) => ({
              ...r,
              key: r.key ?? r.search_query ?? r.id ?? idx.toString(),
            })),
          );
        }
      } catch {
        if (offset === 0) setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [offset, activeTab, isAsinSelect, keywordsApi, perfParams]);

  useEffect(checkNeedMore, [data, totalRow, bodyHeight, checkNeedMore]);

  const handleExpand = async (expanded: boolean, record: RowData) => {
    if (!expanded) {
      setExpandedRowKeys((prev) => prev.filter((k) => k !== record.key));
      return;
    }

    if (record.level !== 0) {
      setExpandedRowKeys((prev) => [...prev, record.key]);
      return;
    }

    setIsLoading(true);
    try {
      const commonArgs = perfParams.slice(0, 5);
      const filtersArg = perfParams[6];
      const raw = isAsinSelect
        ? await keywordsApi.getKeywordPerfomanceAsinSearchQuery(
            ...commonArgs,
            record.key,
            filtersArg,
          )
        : await keywordsApi.getKeywordPerfomanceBrandSearchQuery(
            ...commonArgs,
            record.key,
            filtersArg,
          );

      const childrenRows: RowData[] = raw.map((c, i) => ({
        ...c,
        key: c.key ?? c.search_query ?? `${record.key}-${i}`,
        level: 1,
      }));

      setData((prev) =>
        prev.map((item) =>
          item.key === record.key ? { ...item, children: childrenRows } : item,
        ),
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }

    setExpandedRowKeys((prev) => [...prev, record.key]);
  };

  const toggleSort = useCallback(() => {
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    setOffset(0);
    setHasMore(true);
  }, []);

  const columns = useMemo(() => {
    const base = getTableColumns(activeTab, isWhatIf);
    if (!base.length) return base;
    return base.map((col) => {
      if (typeof col.title === "string" && col.title === "Search Volume") {
        return {
          ...col,
          title: (
            <div className={styles.colTitle}>
              {col.title}
              <SortToggle dir={sortDir} onToggle={toggleSort} />
            </div>
          ),
        };
      }
      return col;
    });
  }, [activeTab, isWhatIf, sortDir, toggleSort]);

  const dataSource = useMemo(
    () => (totalRow ? [...data, totalRow] : data),
    [data, totalRow],
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.header} ref={headerRef}>
        <h3>Organic Keyword Performance</h3>
      </div>
      <div className={styles.tabs} ref={tabsRef}>
        <Tabs
          tabs={availableTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>
      <div className={styles.tableWrapper} ref={wrapperRef}>
        <ExpandableTable
          rowKey="key"
          columns={columns}
          data={dataSource}
          isLoading={isLoading}
          scroll={{ x: "3900px", y: `${bodyHeight - 200}px` }}
          rowSelection={{
            selectedRowKeys: selections,
            onChange: setSelections,
            hideSelectAll: true,
            getCheckboxProps: (rec) => ({
              disabled: rec.key === "Total",
              style: rec.key === "Total" ? { display: "none" } : undefined,
            }),
          }}
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) => {
              void handleExpand(expanded, record);
            },
            rowExpandable: (record: RowData) => record.level === 0,
          }}
        />
      </div>
    </div>
  );
};
